import React, { useState, useEffect } from 'react';
import './HardwareHistory.css';
import TeamHeader from '../TeamHeader/TeamHeader';
import { useDetectorRelations } from "./useDetectorRelations";
import { useAuth } from '../../Auth';
import { useLocation } from "react-router-dom";

// Icons
import { PiFilesLight, PiImage } from "react-icons/pi";
import { GoPencil } from "react-icons/go";
import { FaPlus, FaRegTrashAlt } from "react-icons/fa";

// Toast imports
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';


interface HardwareNote {
    note_id: number;
    maintenanced_by: number;
    note_related_detector: string;
    note_date_and_time: string;
    note_description: string;
    note_parameters: any;
    images?: { id: number; image: string }[];
    files?: { id: number; file: string }[];
}

interface FNNUsers {
    id: number;
    user_id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
}

type UploadItem =
    | File
    | {
        id: number;
        previewUrl: string;
    };


const formatDateTime = (isoString: string) => {
    if (!isoString) return "N/A";
    const date = new Date(isoString);
    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
};

function displayNotification(successOrFail: boolean, message: string) {
    if (successOrFail) {
        toast.success(message, {
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            style: {
                backgroundColor: '#e7ecf1ff',
                color: 'black',
                fontSize: '16px',
            }
        });
    }
    else {
        toast.error(message, {
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            style: {
                backgroundColor: '#e7ecf1ff',
                color: 'black',
                fontSize: '16px',
            }
        });
    }
}

const HardwareHistory = () => {


    // -------------------------- OPENING LINK FROM DETECTOR MANAGEMENT PAGE --------------------------
    const location = useLocation();
    const params = new URLSearchParams(location.search);

    const initialSearch = params.get("search") || "";

    useEffect(() => {
        if (initialSearch) {
            window.history.replaceState({}, "", "/hardware/history");
        }
    }, [initialSearch]);

    const truncateText = (text: string, maxLength: number) =>
        text.length > maxLength ? text.slice(0, maxLength) + "..." : text;

    // -------------------------- OPENING LINK FROM DETECTOR MANAGEMENT PAGE --------------------------

    // Hardware History table
    const now = new Date();
    const [expandedRow, setExpandedRow] = useState<number | null>(null);
    const [hardwareNotes, setHardwareNotes] = useState<HardwareNote[]>([]);
    const [showPopup, setShowPopup] = useState(false);
    const [showDeleteItemPopup, setShowDeleteItemPopup] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [hardwareSearchTerm, setHardwareSearchTerm] = useState(initialSearch);
    const [selectedDetector, setSelectedDetector] = useState<any>(null);
    const [maintenancedBy, setMaintenancedBy] = useState('');
    const [noteDescription, setNoteDescription] = useState('');
    const [noteDate, setNoteDate] = useState(new Date().toISOString().slice(0, 10));
    const [noteTime, setNoteTime] = useState(now.toTimeString().slice(0, 5));
    const [imageFiles, setImageFiles] = useState<UploadItem[]>([]);
    const [docFiles, setDocFiles] = useState<UploadItem[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editingNote, setEditingNote] = useState<HardwareNote | null>(null);
    // Time filter
    const [timeFilter, setTimeFilter] = useState<'1d' | '1w' | '2w' | '1m' | 'all'>('all');

    // Actions
    const [selectedNotes, setSelectedNotes] = useState<number[]>([]);
    const [showActionsDropdown, setShowActionsDropdown] = useState(false);


    // Calling the full detector relation
    const { fullDetectors } = useDetectorRelations();

    const toggleRow = (id: number) => {
        setExpandedRow(prev => (prev === id ? null : id));
    };

    const fetchCsrfToken = async () => {
        const response = await fetch('/api/csrf-token/', {
            method: 'GET',
            credentials: "same-origin", // include cookies in the request
        });

        if (response.ok) {
            const data = await response.json();
            return data.csrfToken; // Return the CSRF token
        } else {
            console.error('Failed to fetch CSRF token');
            return null;
        }
    };

    const fetchHardwareNotes = async () => {
        try {
            const response = await fetch('/api/hardware_history/', {
                credentials: "same-origin"
            });
            const data = await response.json();
            if (response.ok) {
                const parsedData = data.map((n: HardwareNote) => ({
                    ...n,
                    note_parameters: typeof n.note_parameters === 'string'
                        ? JSON.parse(n.note_parameters)
                        : n.note_parameters
                }));
                setHardwareNotes(parsedData);
            } else {
                console.error("Failed to fetch hardware notes:", data.error || response.statusText);
            }
        } catch (error) {
            console.error("❌ Error fetching hardware notes:", error);
        }
    };

    useEffect(() => {
        fetchHardwareNotes();
    }, []);


    // Fetching the list of FNN users
    const [fnnUsers, setFnnUsers] = useState<FNNUsers[]>([]);
    const { first_name, last_name, email, username } = useAuth();

    const fetchFNNUsers = async () => {
        try {
            const response = await fetch('/api/inventory/fnn-users/', {
                credentials: "same-origin"
            });
            const data = await response.json();
            if (response.ok) {
                setFnnUsers(data);
            } else {
                console.error("Failed to load fnn users:", data.error || response.statusText);
            }
        } catch (error) {
            console.error("❌ Error fetching fnn users:", error);
        }
    };

    useEffect(() => {
        fetchFNNUsers();
    }, []);

    const loggedFnnUserId = React.useMemo(() => {
        if (!fnnUsers?.length) return null;

        const matchedUser = fnnUsers.find(
            (user) =>
                user.username === username &&
                user.first_name === first_name &&
                user.last_name === last_name
        );

        return matchedUser ? matchedUser.id : null;
    }, [fnnUsers, username, first_name, last_name]);

    const [selectedMaintainer, setSelectedMaintainer] = useState(loggedFnnUserId);
    const [showDetectorSuggestions, setShowDetectorSuggestions] = useState(false);
    const [showMaintainerSuggestions, setShowMaintainerSuggestions] = useState(false);

    const getFnnUserName = (userId: string | number | null | undefined) => {
        if (!userId || !fnnUsers?.length) return "—";
        const user = fnnUsers.find((u) => String(u.id) === String(userId));
        if (!user) return "—";
        return `${user.first_name} ${user.last_name}`.trim() || user.username || "—";
    };

    // ------------------------------------------------- Handling user searching -------------------------------------------------

    // Filter users for search
    const filteredFnnUsers = (fnnUsers || []).filter((u) => {
        const name = `${u.first_name} ${u.last_name}`.toLowerCase();
        const usernameMatch = u.username?.toLowerCase().includes(maintenancedBy.toLowerCase());
        const nameMatch = name.includes(maintenancedBy.toLowerCase());
        return usernameMatch || nameMatch;
    });

    // Handle user select
    const handleSelectMaintainer = (user: FNNUsers) => {
        setSelectedMaintainer(user.id);
        setMaintenancedBy(`${user.first_name} ${user.last_name}`.trim() || user.username);
    };

    React.useEffect(() => {
        if (loggedFnnUserId) {
            const currentUserName = getFnnUserName(loggedFnnUserId);
            setMaintenancedBy(currentUserName);
            setSelectedMaintainer(loggedFnnUserId);
        }
    }, [loggedFnnUserId]);

    // ------------------------------------------------- Handling user searching -------------------------------------------------

    // ------------------------------------------------- Handling Detector searching -------------------------------------------------
    // --- Search Matching ---
    const term = searchTerm.toLowerCase();

    const filteredDetectors = fullDetectors.filter(fd => {
        const mac = fd.monitoring.mac?.toLowerCase() || "";
        const site_name = fd.monitoring.status?.site_name?.toLowerCase() || "";
        const code = fd.code?.code?.toLowerCase() || "";
        const receiver = fd.code?.receiver?.toLowerCase() || "";
        const name = fd.code?.name?.toLowerCase() || "";

        return (
            mac.includes(term) ||
            site_name.includes(term) ||
            code.includes(term) ||
            receiver.includes(term) ||
            name.includes(term)
        );
    });


    // --- Helper function inside HardwareHistory component ---
    const getFullDetector = (note: HardwareNote) => {
        if (!fullDetectors || !note.note_related_detector) return undefined;

        const [noteDetId, noteDetCode] = note.note_related_detector.split("_");

        return fullDetectors.find(fd => {
            const fdCode = fd.code?.code || fd.monitoring.mac;
            const fdId = fd.code?.id || fd.monitoring.mac;
            return fdId === noteDetId && fdCode === noteDetCode;
        });
    };



    const handleSelectDetector = (det: any) => {
        setSelectedDetector(det);

        // Display "id_code" format in the input
        const detId = det.code?.id || det.id;
        const detCode = det.code?.code || det.monitoring.mac;
        setSearchTerm(`${detId}_${detCode}`);
    };
    // ------------------------------------------------- Handling Detector searching -------------------------------------------------


    // --- Handle file uploads ---
    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) {
            setImageFiles(prev => [...prev, ...Array.from(e.target.files!)]);
        }
    };

    const handleDocUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) {
            setDocFiles(prev => [...prev, ...Array.from(e.target.files!)]);
        }
    };

    const removeImage = (idx: number) => {
        setImageFiles(prev => prev.filter((_, i) => i !== idx));
    };
    const removeDoc = (idx: number) => {
        setDocFiles(prev => prev.filter((_, i) => i !== idx));
    };

    // --- Construct note_parameters ---
    const buildNoteParameters = (det: any) => {
        const mon = det.monitoring;
        const stat = det.code;
        const built = det.built;
        const subs: { id: number; subassembly?: { id: number } | number }[] = det.subsBom || [];

        // Find the most recent hardware note for this detector
        const detectorIdCode = `${det.code?.id || det.id}_${det.code?.code || det.monitoring.mac}`;
        const previousNotes = hardwareNotes
            .filter(n => n.note_related_detector === detectorIdCode)
            .sort((a, b) => new Date(b.note_date_and_time).getTime() - new Date(a.note_date_and_time).getTime());

        const lastMaintenanceDate = previousNotes.length > 0
            ? previousNotes[0].note_date_and_time.split('T')[0]  // just the date part
            : null;

        return {
            mac: mon.mac,
            gps_fix: mon.status.gps_fix,
            gps_lat: mon.status.gps_lat,
            gps_long: mon.status.gps_long,
            site_name: stat.name,
            memory: parseFloat(mon.status.memory) || 0,
            fw_version: mon.status.fw_version,
            sim_card_no: stat.sim_card || "N/A",
            online: mon.online,
            last_maintenance: lastMaintenanceDate,   // <-- use previous note date
            installation_date: formatDateTime(stat.created_at),
            hardware_version: det.detectorBom?.version || "Unknown",
            files_transferred: 0,
            peak_algo: mon.status.peak_algo,
            dacq_algo: mon.status.dacq_algo,
            file_algo: mon.status.file_algo,
            csv_algo: mon.status.csv_algo,
            built_subassemblies: subs.map((s) => s.id),
            subassemblies: subs.map((s) => s.subassembly && typeof s.subassembly === "object"
                ? s.subassembly.id
                : s.subassembly)
        };
    };

    // --- POST request ---
    const handleSubmit = async () => {

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        if (!selectedDetector) {
            toast.warning("Please select a detector first.", {
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                style: {
                    backgroundColor: '#e7ecf1ff',
                    color: 'black',
                    fontSize: '16px',
                }
            });
            return;
        }

        // Convert the local date/time from the inputs to server time (ISO string)
        const localDateTime = new Date(`${noteDate}T${noteTime}`);
        // Send as UTC (server time)
        const serverDateTime = localDateTime.toISOString();

        const formData = new FormData();
        const detCode = selectedDetector.code?.code || "N/A";
        const detId = selectedDetector.code?.id || selectedDetector.id;

        formData.append("note_related_detector", `${detId}_${detCode}`);
        formData.append("maintenanced_by", String(selectedMaintainer || ""));
        formData.append("note_date_and_time", serverDateTime);
        formData.append("note_description", noteDescription);
        formData.append("note_parameters", JSON.stringify(buildNoteParameters(selectedDetector)));

        imageFiles.forEach(img => {
            if (img instanceof File) formData.append("images", img);
        });
        docFiles.forEach(file => {
            if (file instanceof File) formData.append("files", file);
        });


        try {
            const res = await fetch("/api/hardware_history/", {
                method: "POST",
                credentials: "same-origin",
                headers: {
                    'X-CSRFToken': csrfToken
                },
                body: formData,
            });

            if (res.ok) {
                displayNotification(true, "Note added successfully!.");
                setShowPopup(false);
                fetchHardwareNotes();
                // Reset form
                setSelectedDetector(null);
                setMaintenancedBy("");
                setNoteDescription("");
                setImageFiles([]);
                setDocFiles([]);
            } else {
                const err = await res.json();
                console.error("❌ Error adding note:", err);
                displayNotification(false, "Failed to add note.");
            }
        } catch (err) {
            console.error("❌ Network error:", err);
            displayNotification(false, "Network error while saving note.");
        }
    };

    // --- PUT request ---
    const handleEditClick = (note: HardwareNote) => {
        setIsEditing(true);
        setEditingNote(note);
        setShowPopup(true);

        const det = getFullDetector(note);
        setSelectedDetector(det || null);
        setSearchTerm(note.note_related_detector);

        const maintainerName = getFnnUserName(note.maintenanced_by);
        setMaintenancedBy(maintainerName);
        setSelectedMaintainer(note.maintenanced_by);

        const dateObj = new Date(note.note_date_and_time);
        setNoteDate(dateObj.toISOString().slice(0, 10));
        setNoteTime(dateObj.toTimeString().slice(0, 5));

        setNoteDescription(note.note_description);
        // Load existing images and files
        setImageFiles(note.images?.map(img => ({ id: img.id, previewUrl: img.image })) || []);
        setDocFiles(note.files?.map(file => ({ id: file.id, previewUrl: file.file })) || []);
    };

    const handleUpdate = async () => {
        if (!editingNote) return;

        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        const localDateTime = new Date(`${noteDate}T${noteTime}`);
        const serverDateTime = localDateTime.toISOString();

        // Type guard to check if item is an existing file (has id)
        const isExistingUpload = (item: UploadItem): item is { id: number; previewUrl: string } => {
            return !(item instanceof File);
        };

        const keptImageIds = imageFiles
            .filter(isExistingUpload)  // now TS knows this is the {id, previewUrl} type
            .map(img => String(img.id));

        const keptFileIds = docFiles
            .filter(isExistingUpload)
            .map(file => String(file.id));


        const formData = new FormData();
        formData.append("note_related_detector", editingNote.note_related_detector);
        formData.append("maintenanced_by", String(selectedMaintainer || ""));
        formData.append("note_date_and_time", serverDateTime);
        formData.append("note_description", noteDescription);
        formData.append("note_parameters", JSON.stringify(editingNote.note_parameters));

        // Send only the kept (not removed) IDs
        keptImageIds.forEach(id => formData.append("existing_image_ids", id));
        keptFileIds.forEach(id => formData.append("existing_file_ids", id));

        // Add new uploads
        imageFiles.forEach(img => {
            if (img instanceof File) formData.append("images", img);
        });
        docFiles.forEach(file => {
            if (file instanceof File) formData.append("files", file);
        });

        try {
            const res = await fetch(`/api/hardware_history/${editingNote.note_id}/`, {
                method: "PUT",
                credentials: "same-origin",
                headers: { 'X-CSRFToken': csrfToken },
                body: formData,
            });

            if (res.ok) {
                displayNotification(true, "Note updated successfully!");
                setShowPopup(false);
                setIsEditing(false);
                fetchHardwareNotes();
            } else {
                const err = await res.json();
                console.error("❌ Error updating note:", err);
                displayNotification(false, "Failed to update note");
            }
        } catch (err) {
            console.error("❌ Network error:", err);
            displayNotification(false, "Network error while updating note.");
        }
    };



    const getSubassemblyName = (id: number) => {
        const sub = fullDetectors
            .flatMap(fd => fd.subsBom || [])
            .find(sub => sub.subassembly?.id === id);
        return sub?.subassembly?.name || "Unknown";
    };

    const clearUpdateForm = () => {
        setSelectedDetector(null);
        setSearchTerm("");
        setSelectedMaintainer(loggedFnnUserId); // reset to logged-in user
        setMaintenancedBy(getFnnUserName(loggedFnnUserId)); // reset name
        setNoteDescription("");
        setImageFiles([]); // clear uploaded images
        setDocFiles([]);   // clear uploaded files
        setNoteDate(new Date().toISOString().slice(0, 10)); // optional: reset date
        setNoteTime(new Date().toTimeString().slice(0, 5)); // optional: reset time
    };

    // Filter hardware notes based on searchTerm
    const filteredHardwareNotes = hardwareNotes.filter(note => {
        if (!note.note_parameters) return false;

        const search = hardwareSearchTerm.toLowerCase();

        const matchesSearch =
            !search ||
            note.note_related_detector.toLowerCase().includes(search) ||
            note.note_parameters.site_name?.toLowerCase().includes(search) ||
            note.note_parameters.mac?.toLowerCase().includes(search);

        if (!matchesSearch) return false;

        // time filtering
        if (timeFilter === "all") return true;

        const noteDate = new Date(note.note_date_and_time);
        const now = new Date();
        const threshold = new Date();

        switch (timeFilter) {
            case "1d": threshold.setDate(now.getDate() - 1); break;
            case "1w": threshold.setDate(now.getDate() - 7); break;
            case "2w": threshold.setDate(now.getDate() - 14); break;
            case "1m": threshold.setMonth(now.getMonth() - 1); break;
        }

        return noteDate >= threshold;
    });



    return (
        <div className='customer-directory-body'>
            <TeamHeader title="Hardware" />
            <ToastContainer />

            <h1 className='cutomer-title'>Hardware History</h1>

            <div className='hardware3-action-buttons'>

                {/* Search Input */}

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem', paddingRight: '1rem' }}>
                    <input
                        type="text"
                        placeholder="Search ..."
                        value={hardwareSearchTerm}
                        onChange={(e) => setHardwareSearchTerm(e.target.value)}
                        className="hardware3-open-popup-button"
                        style={{ marginRight: '0.5rem' }}
                    />

                    <select
                        value={timeFilter}
                        onChange={(e) => setTimeFilter(e.target.value as typeof timeFilter)}
                        className="hardware3-open-popup-button hardware3-filter-select"
                    >
                        <option value="1d">1 Day</option>
                        <option value="1w">1 Week</option>
                        <option value="2w">2 Weeks</option>
                        <option value="1m">1 Month</option>
                        <option value="all">All Time</option>
                    </select>

                </div>


                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button
                        className="hardware3-open-popup-button"
                        onClick={() => {
                            clearUpdateForm();
                            setIsEditing(false);
                            setShowPopup(true);
                        }}
                    >
                        <FaPlus />
                        New Entry
                    </button>

                    <div className="actions-dropdown-wrapper" style={{ position: 'relative' }}>
                        <select
                            className="hardware3-open-popup-button hardware3-filter-select"
                            value=""
                            onChange={async (e) => {
                                const action = e.target.value;
                                e.target.value = ""; // reset select visual

                                if (action === "delete") {
                                    if (selectedNotes.length === 0) {
                                        toast.warning("No entries selected.", {
                                            autoClose: 3000,
                                            hideProgressBar: false,
                                            closeOnClick: true,
                                            pauseOnHover: true,
                                            draggable: true,
                                            style: {
                                                backgroundColor: '#e7ecf1ff',
                                                color: 'black',
                                                fontSize: '16px',
                                            }
                                        });
                                        return;
                                    }

                                    // Show custom delete popup instead of window.confirm
                                    setShowDeleteItemPopup(true);
                                } else if (action === "export") {
                                    if (selectedNotes.length === 0) {
                                        toast.warning("No entries selected.", {
                                            autoClose: 3000,
                                            hideProgressBar: false,
                                            closeOnClick: true,
                                            pauseOnHover: true,
                                            draggable: true,
                                            style: {
                                                backgroundColor: '#e7ecf1ff',
                                                color: 'black',
                                                fontSize: '16px',
                                            }
                                        });
                                        return;
                                    }

                                    // Export selected notes to CSV
                                    const selectedNotesData = hardwareNotes.filter(note =>
                                        selectedNotes.includes(note.note_id)
                                    );

                                    // CSV Headers
                                    const headers = [
                                        "Detector ID and Code",
                                        "MAC Address",
                                        "Maintenanced By",
                                        "Date and Time",
                                        "Location (Site Name)",
                                        "GPS Fix",
                                        "GPS Latitude",
                                        "GPS Longitude",
                                        "Memory (GB)",
                                        "SIM Card Number",
                                        "Installation Date",
                                        "Last Maintenance",
                                        "Hardware Version",
                                        "Firmware Version",
                                        "Files Transferred/Hour",
                                        "Built Subassemblies",
                                        "Subassembly Names",
                                        "Status (Online/Offline)",
                                        "Peak Algorithm",
                                        "DACQ Algorithm",
                                        "File-Transfer Algorithm",
                                        "CSV-Transfer Algorithm",
                                        "Description",
                                        "Number of Images",
                                        "Number of Files"
                                    ];

                                    // Build CSV rows
                                    const rows = selectedNotesData.map(note => {
                                        const params = note.note_parameters;
                                        const subassemblyNames = params?.built_subassemblies?.map((subId: string, idx: number) => {
                                            return getSubassemblyName(params.subassemblies[idx]);
                                        }).join("; ") || "N/A";

                                        // Format date without commas for CSV
                                        const dateObj = new Date(note.note_date_and_time);
                                        const formattedDate = dateObj.toISOString().replace('T', ' ').slice(0, 19);

                                        return [
                                            note.note_related_detector,
                                            params?.mac || "N/A",
                                            getFnnUserName(note.maintenanced_by),
                                            formattedDate,
                                            `"${params?.site_name || "N/A"}"`,
                                            params?.gps_fix ? "Yes" : "No",
                                            params?.gps_lat || "N/A",
                                            params?.gps_long || "N/A",
                                            params?.memory || 0,
                                            params?.sim_card_no || "N/A",
                                            params?.installation_date || "N/A",
                                            params?.last_maintenance ? new Date(params.last_maintenance + "T00:00:00").toLocaleDateString() : "N/A",
                                            params?.hardware_version || "Unknown",
                                            params?.fw_version || "N/A",
                                            params?.files_transferred || 0,
                                            `"${params?.built_subassemblies?.join("; ") || "N/A"}"`,
                                            `"${subassemblyNames}"`,
                                            params?.online ? "Online" : "Offline",
                                            (params?.peak_algo && params?.peak_algo == true) ? "Online" : "Offline",
                                            (params?.dacq_algo && params?.dacq_algo == true) ? "Online" : "Offline",
                                            (params?.file_algo && params?.file_algo == true) ? "Online" : "Offline",
                                            (params?.csv_algo && params?.csv_algo == true) ? "Online" : "Offline",
                                            `"${(note.note_description || "").replace(/"/g, '""')}"`, // Escape quotes in description
                                            note.images?.length || 0,
                                            note.files?.length || 0
                                        ];
                                    });

                                    // Convert to CSV string
                                    const csvContent = [
                                        headers.join(","),
                                        ...rows.map(row => row.join(","))
                                    ].join("\n");

                                    // Create and download CSV file
                                    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
                                    const link = document.createElement("a");
                                    const url = URL.createObjectURL(blob);
                                    link.setAttribute("href", url);
                                    link.setAttribute("download", `hardware_history_${new Date().toISOString().split('T')[0]}.csv`);
                                    link.style.visibility = "hidden";
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);

                                    toast.success(`${selectedNotes.length} entries exported successfully!`, {
                                        autoClose: 3000,
                                        hideProgressBar: false,
                                        closeOnClick: true,
                                        pauseOnHover: true,
                                        draggable: true,
                                        style: {
                                            backgroundColor: '#e7ecf1ff',
                                            color: 'black',
                                            fontSize: '16px',
                                        }
                                    });
                                }

                            }}
                        >
                            <option value="" disabled selected>
                                Actions
                            </option>
                            <option value="delete">Delete</option>
                            <option value="export">Export</option>
                        </select>
                    </div>


                </div>

            </div>


            <div className='hardware3-table-wrapper'>
                <table className='hardware3-table'>
                    <thead>
                        <tr>
                            <th style={{ width: '40px', textAlign: 'center' }}>
                                <input
                                    type="checkbox"
                                    checked={selectedNotes.length === filteredHardwareNotes.length && filteredHardwareNotes.length > 0}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setSelectedNotes(filteredHardwareNotes.map((n) => n.note_id));
                                        } else {
                                            setSelectedNotes([]);
                                        }
                                    }}
                                />
                            </th>

                            <th>Detector ID and Code</th>
                            <th>Site</th>
                            <th>Version</th>
                            <th>Components</th>
                            <th>Status</th>
                            <th>Note</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredHardwareNotes.map((note) => {
                            const params = note.note_parameters;
                            const componentsString = params?.built_subassemblies?.join(", ") || "";
                            const isExpanded = expandedRow === note.note_id;

                            return (
                                <React.Fragment key={note.note_id}>
                                    {isExpanded ? (
                                        // --- Expanded Row replaces main row ---
                                        <tr className='hardware3-expanded-row' onClick={() => toggleRow(note.note_id)} style={{ cursor: 'pointer' }}>
                                            <td style={{ textAlign: 'center' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedNotes.includes(note.note_id)}
                                                    onClick={(e) => e.stopPropagation()} // prevent row expand
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedNotes((prev) => [...prev, note.note_id]);
                                                        } else {
                                                            setSelectedNotes((prev) => prev.filter((id) => id !== note.note_id));
                                                        }
                                                    }}
                                                />
                                            </td>

                                            <td>
                                                <strong>ID and Code:</strong> {note.note_related_detector}<br />
                                                <strong>MAC:</strong> {params?.mac}<br /><br />
                                                <strong>Maintenanced By:</strong> {getFnnUserName(note.maintenanced_by)}<br /><br />
                                                <strong>Date:</strong> {formatDateTime(note.note_date_and_time)}
                                            </td>
                                            <td>
                                                <strong>Detector:</strong> {params?.site_name}<br />
                                                <strong>GPS Fix:</strong> {params?.gps_fix ? "Yes" : "No"}<br />
                                                <strong>Latitude:</strong> {params?.gps_lat}<br />
                                                <strong>Longitude:</strong> {params?.gps_long}<br />
                                                <strong>Memory:</strong> {params?.memory} GB<br />
                                                <strong>SIM Card:</strong> {params?.sim_card_no || "N/A"}<br />
                                                <strong>Installation Date:</strong> {params?.installation_date || "N/A"}<br />
                                                <strong>Last Maintenance:</strong> {params?.last_maintenance
                                                    ? new Date(params.last_maintenance + "T00:00:00").toLocaleDateString()
                                                    : "N/A"}<br />

                                            </td>
                                            <td>
                                                <span>
                                                    <strong>Hardware Version: </strong>
                                                    <span
                                                        style={{
                                                            cursor: getFullDetector(note)?.detectorBom?.id ? "pointer" : "default",
                                                            color: getFullDetector(note)?.detectorBom?.id ? "#28aee1" : "inherit",
                                                            textDecoration: getFullDetector(note)?.detectorBom?.id ? "underline" : "none"
                                                        }}
                                                        onClick={() => {
                                                            const det = getFullDetector(note);
                                                            if (det?.detectorBom?.id) {
                                                                window.open(`/inventory?openBOM=true&det=${det.detectorBom.id}`, "_blank");
                                                            }
                                                        }}
                                                    >
                                                        {params?.hardware_version}
                                                    </span>
                                                </span>
                                                <br />
                                                <strong>Firmware Version:</strong> {params?.fw_version}<br />
                                                <strong>Files transferred/hour:</strong> {params?.files_transferred}<br />
                                            </td>
                                            <td>
                                                {params?.built_subassemblies && params.built_subassemblies.length > 0 ? (
                                                    params.built_subassemblies.map((subId: string, idx: number) => {
                                                        const name = getSubassemblyName(params.subassemblies[idx]) || "Unknown";
                                                        return (
                                                            <div key={subId}>
                                                                <strong>{subId}:</strong>{" "}
                                                                <u
                                                                    style={{ cursor: 'pointer', color: '#28aee1' }}
                                                                    onClick={() => window.open(`/inventory?openBOM=true&sub=${encodeURIComponent(name)}`, "_blank")}
                                                                >
                                                                    {name}
                                                                </u>
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <span style={{ fontStyle: 'italic' }}>
                                                        Components for this detector have not been mapped to inventory yet.
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <strong>Status:</strong> <span className={params?.online ? 'status-online' : 'status-offline'}>{params?.online ? "Online" : "Offline"}</span><br />
                                                <strong>Peak algorithm:</strong> <span className={(params?.peak_algo && params?.peak_algo == true) ? 'status-online' : 'status-offline'}>{(params?.peak_algo && params?.peak_algo == true) ? "Online" : "Offline"}</span><br />
                                                <strong>DACQ algorithm:</strong> <span className={(params?.dacq_algo && params?.dacq_algo == true) ? 'status-online' : 'status-offline'}>{(params?.dacq_algo && params?.dacq_algo == true) ? "Online" : "Offline"}</span><br />
                                                <strong>File-transfer algorithm:</strong> <span className={(params?.file_algo && params?.file_algo == true) ? 'status-online' : 'status-offline'}>{(params?.file_algo && params?.file_algo == true) ? "Online" : "Offline"}</span><br />
                                                <strong>CSV-transfer algorithm:</strong> <span className={(params?.csv_algo && params?.csv_algo == true) ? 'status-online' : 'status-offline'}>{(params?.csv_algo && params?.csv_algo == true) ? "Online" : "Offline"}</span><br />
                                            </td>
                                            <td className="column-with-pencil">
                                                <div className="description-with-pencil">
                                                    <span>
                                                        <strong>Description:</strong> {note.note_description}<br />

                                                        {note.files?.length ? (
                                                            <>
                                                                <strong>Files:</strong>
                                                                <ul className='hardware3-expanded-row-images'>
                                                                    {(note.files || []).map(f => {
                                                                        const fileName = f.file.split('/').pop();
                                                                        return (
                                                                            <li key={f.id} className='li-file'>
                                                                                <a
                                                                                    href={f.file}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className='upload-item upload-item-in-table'
                                                                                >
                                                                                    <PiFilesLight className='upload-item-icon' />
                                                                                    <div>{fileName}</div>
                                                                                </a>
                                                                            </li>
                                                                        );
                                                                    })}
                                                                </ul>
                                                            </>
                                                        ) : null}

                                                        {note.images?.length ? (
                                                            <>
                                                                <strong>Images:</strong>
                                                                <ul className='hardware3-expanded-row-images'>
                                                                    {(note.images || []).map(img => (
                                                                        <li key={img.id}>
                                                                            <a href={img.image} target="_blank" rel="noopener noreferrer">
                                                                                <img src={img.image} alt='' />
                                                                            </a>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </>
                                                        ) : null}
                                                    </span>

                                                    {/* Pencil button on the right side of the column */}
                                                    <button
                                                        className="edit-note-btn"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEditClick(note);
                                                        }}
                                                    >
                                                        <GoPencil size={18} />
                                                    </button>
                                                </div>
                                            </td>


                                        </tr>
                                    ) : (
                                        // --- Main Row ---
                                        <tr onClick={() => toggleRow(note.note_id)} style={{ cursor: 'pointer' }}>
                                            <td style={{ textAlign: 'center' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedNotes.includes(note.note_id)}
                                                    onClick={(e) => e.stopPropagation()} // prevent row expand
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedNotes((prev) => [...prev, note.note_id]);
                                                        } else {
                                                            setSelectedNotes((prev) => prev.filter((id) => id !== note.note_id));
                                                        }
                                                    }}
                                                />
                                            </td>

                                            <td>{note.note_related_detector}</td>
                                            <td>{params?.site_name}</td>
                                            <td>
                                                <div className='hardware3-table-inner-td'>
                                                    <p><strong>Software: </strong>{params?.fw_version}</p>
                                                    <p><strong>Hardware: </strong>{params?.hardware_version}</p>
                                                </div>
                                            </td>
                                            <td>
                                                {params?.built_subassemblies?.length > 0 ? (
                                                    componentsString
                                                ) : (
                                                    <span style={{ fontStyle: 'italic' }}>
                                                        Components for this detector have not been mapped to inventory yet.
                                                    </span>
                                                )}
                                            </td>
                                            <td className={params?.online ? 'status-online' : 'status-offline'}>
                                                {params?.online ? "Online" : "Offline"}
                                            </td>
                                            <td className="column-with-pencil">
                                                <div className="description-with-pencil">
                                                    <span><strong>{formatDateTime(note.note_date_and_time)}<br /></strong>{truncateText(note.note_description, 60)}</span>
                                                    <button
                                                        className="edit-note-btn"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEditClick(note);
                                                        }}
                                                    >
                                                        <GoPencil size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>

                </table>
            </div>

            {/* Add/Edit Popup */}
            {showPopup && (
                <div className="hh-popup-overlay">
                    <div className="hh-popup-content">

                        <div>
                            <h2>{isEditing ? "Edit Update" : "Add New Update"}</h2>
                            <p>{isEditing ? "Modify an existing record." : "Create a record for new updates."}</p>

                            <div className='hh-form'>

                                {/* Detector Search */}
                                <div className='hh-duo'>

                                    <label><strong>Detector ID and Code</strong>
                                        <input
                                            type="text"
                                            placeholder="Search by mac, site name, or code..."
                                            value={searchTerm}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setSearchTerm(val);
                                                if (!val || (selectedDetector && !val.includes(selectedDetector.code?.code))) {
                                                    setSelectedDetector(null);
                                                }
                                                setShowDetectorSuggestions(true);
                                            }}
                                            onFocus={() => setShowDetectorSuggestions(true)}
                                            onBlur={() => setTimeout(() => setShowDetectorSuggestions(false), 150)}
                                        />

                                        {showDetectorSuggestions && filteredDetectors.length > 0 && (
                                            <ul className="hh-search-results">
                                                {filteredDetectors.map((det, idx) => (
                                                    <li key={idx} onClick={() => { handleSelectDetector(det); setShowDetectorSuggestions(false); }}>
                                                        {det.monitoring.mac} — {det.code?.name} ({det.code?.code || "No code"})
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </label>

                                    <label>
                                        <strong>Maintenanced By</strong>
                                        <input
                                            type="text"
                                            placeholder="Search technician or team..."
                                            value={maintenancedBy}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setMaintenancedBy(val);
                                                if (!val || (selectedMaintainer && !val.toLowerCase().includes(getFnnUserName(selectedMaintainer)?.toLowerCase()))) {
                                                    setSelectedMaintainer(null);
                                                }
                                                setShowMaintainerSuggestions(true);
                                            }}
                                            onFocus={() => setShowMaintainerSuggestions(true)}
                                            onBlur={() => setTimeout(() => setShowMaintainerSuggestions(false), 150)}
                                        />

                                        {showMaintainerSuggestions && filteredFnnUsers.length > 0 && !selectedMaintainer && (
                                            <ul className="hh-search-results">
                                                {filteredFnnUsers.map((user) => (
                                                    <li key={user.id} onClick={() => { handleSelectMaintainer(user); setShowMaintainerSuggestions(false); }}>
                                                        {user.first_name} {user.last_name} ({user.username})
                                                    </li>
                                                ))}
                                            </ul>
                                        )}

                                    </label>

                                </div>


                                {selectedDetector && (
                                    <div className="hh-detector-info">
                                        <p>
                                            <strong>Selected:</strong>{" "}
                                            {selectedDetector.monitoring.mac} — {selectedDetector.code?.name} ({selectedDetector.code?.code || "N/A"})
                                        </p>
                                    </div>
                                )}

                                {/* Date/Time */}
                                <div className='hh-duo'>
                                    <label>
                                        <strong>Date</strong>
                                        <input type="date" value={noteDate} onChange={(e) => setNoteDate(e.target.value)} />
                                    </label>
                                    <label>
                                        <strong>Time</strong>
                                        <input type="time" value={noteTime} onChange={(e) => setNoteTime(e.target.value)} />
                                    </label>
                                </div>

                                {/* Description */}
                                <label><strong>Description</strong>
                                    <textarea
                                        rows={3}
                                        value={noteDescription}
                                        onChange={(e) => setNoteDescription(e.target.value)}
                                        placeholder="Describe maintenance or hardware update..."
                                    />
                                </label>

                                {/* Uploads */}
                                <div className="hh-duo hh-uploads">
                                    <div className="upload-block">
                                        <strong>Images:</strong>
                                        <label className="upload-btn">
                                            <input type="file" accept="image/*" multiple onChange={handleImageUpload} />
                                            <span>Upload Image</span>
                                        </label>
                                        <div className="upload-list">
                                            {imageFiles.map((f, i) => {
                                                const displayName =
                                                    f instanceof File
                                                        ? f.name
                                                        : f.previewUrl.split("/").pop(); // extract filename from URL
                                                return (
                                                    <span key={i} className="upload-item">
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                                                            <PiImage className='upload-item-icon' /> {displayName}
                                                        </div>
                                                        <button onClick={() => removeImage(i)}>✕</button>
                                                    </span>
                                                );
                                            })}
                                        </div>

                                    </div>
                                    <div className="upload-block">
                                        <strong>Files:</strong>
                                        <label className="upload-btn">
                                            <input type="file" multiple onChange={handleDocUpload} />
                                            <span>Upload File</span>
                                        </label>
                                        <div className="upload-list">
                                            {docFiles.map((f, i) => {
                                                const displayName =
                                                    f instanceof File
                                                        ? f.name
                                                        : f.previewUrl.split("/").pop();
                                                return (
                                                    <span key={i} className="upload-item">
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                                                            <PiFilesLight className='upload-item-icon' /> {displayName}
                                                        </div>
                                                        <button onClick={() => removeDoc(i)}>✕</button>
                                                    </span>
                                                );
                                            })}
                                        </div>

                                    </div>
                                </div>

                            </div>
                        </div>

                        {/* Actions */}
                        <div className="hh-duo hh-actions">
                            <button className="cancel-btn"
                                onClick={() => {
                                    clearUpdateForm();
                                    setShowPopup(false);
                                }}

                            >
                                Cancel</button>
                            <button className="confirm-btn" onClick={isEditing ? handleUpdate : handleSubmit}>
                                {isEditing ? "Save Changes" : "Confirm"}
                            </button>

                        </div>

                    </div>
                </div>
            )}

            {/* Delete Popup */}
            {showDeleteItemPopup && (
                <div className='add-item-popup-overlay'>
                    <div className='add-item-popup-content hh-del-pop'>
                        <div className='hh-delete-inner-pop'>
                            <div className='hh-delete-text-and-icon'>
                                <FaRegTrashAlt className='hh-delete-icon' />
                                <div>
                                    <h3>Delete {selectedNotes.length} selected note(s)?</h3>
                                    <p>Are you sure you want to delete the selected note(s)? This action cannot be undone.</p>
                                </div>
                            </div>

                            <div className="hh-duo hh-actions">
                                <button
                                    className="cancel-btn"
                                    onClick={() => setShowDeleteItemPopup(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="hh-delete-btn"
                                    onClick={async () => {
                                        const csrfToken = await fetchCsrfToken();
                                        for (const id of selectedNotes) {
                                            await fetch(`/api/hardware_history/${id}/`, {
                                                method: "DELETE",
                                                credentials: "same-origin",
                                                headers: { "X-CSRFToken": csrfToken },
                                            });
                                        }

                                        displayNotification(true, "Selected entries deleted successfully!");
                                        setSelectedNotes([]);
                                        fetchHardwareNotes();
                                        setShowDeleteItemPopup(false);
                                    }}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default HardwareHistory;
